import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AccountService from '@/services/AccountService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            setIsLoading(false);
            return;
        }
        AccountService.getMe()
            .then(user => setCurrentUser(user))
            .catch(() => localStorage.removeItem('token'))
            .finally(() => setIsLoading(false));
    }, []);

    const login = useCallback(async (email, password) => {
        const result = await AccountService.login(email, password);
        if (result?.requiresTwoFactor) {
            return {
                success: false,
                requiresTwoFactor: true,
                pendingToken: result.pendingToken,
                twoFactorMethod: result.twoFactorMethod ?? 'Totp',
            };
        }
        if (result?.token) {
            localStorage.setItem('token', result.token);
            const user = await AccountService.getMe();
            setCurrentUser(user);
            return { success: true };
        }
        return { success: false, error: result?.message || 'Invalid email or password' };
    }, []);

    const completeTwoFactorLogin = useCallback(async (pendingToken, code) => {
        const result = await AccountService.verifyTwoFactorLogin(pendingToken, code);
        if (result?.token) {
            localStorage.setItem('token', result.token);
            const user = await AccountService.getMe();
            setCurrentUser(user);
            return { success: true };
        }
        return { success: false, error: result?.message || 'Invalid code' };
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        setCurrentUser(null);
        navigate('/login');
    }, [navigate]);

    const updateCurrentUser = useCallback((partial) => {
        setCurrentUser((prev) => (prev ? { ...prev, ...partial } : prev));
    }, []);

    return (
        <AuthContext.Provider value={{
            currentUser,
            isLoading,
            isAuthenticated: !!currentUser,
            isOwner: currentUser?.role === 'Owner' || currentUser?.role === 'Admin',
            login,
            completeTwoFactorLogin,
            logout,
            updateCurrentUser,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
